import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, Box, Typography } from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import { X } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import AppButton from "../../../common_components/AppButton";
import CommonLoader from "../../../common_components/CommonLoader";
import { NumberInput, FormRow, FormField } from "../../../common_components/FormInputs";
import SingleDatePicker from "../../../common_components/daterangepicker/SingleDatePicker";
import { fetchLegacyDataMonth, updateLegacyDataCell, selectLegacyDataSaving, } from "../../../redux/salesReportingSlice";
import { toCalendarDate, fromCalendarDate } from "../Component/shared";
// BDR measures, in the order requested: Enrollment → Outreach → Booked → Happened.
const MEASURE_FIELDS = [
    { key: "enrollments", label: "Enrollment" },
    { key: "outreach", label: "Outreach" },
    { key: "booked", label: "Meeting Booked" },
    { key: "happened", label: "Meetings Happened" },
];
const EMPTY_FORM = { outreach: "", booked: "", happened: "", enrollments: "" };
const pad = (n) => String(n).padStart(2, "0");
const LegacyDataEditModal = ({ open, context, onClose }) => {
    const dispatch = useDispatch();
    const saving = useSelector(selectLegacyDataSaving);
    const [monthMap, setMonthMap] = useState({});
    const [availableDates, setAvailableDates] = useState([]);
    const [selectedDate, setSelectedDate] = useState("");
    const [form, setForm] = useState(EMPTY_FORM);
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});
    // Load every daily record for the clicked entity + month; only these dates are selectable.
    useEffect(() => {
        if (!open || !context)
            return;
        const firstOfMonth = `${context.year}-${pad(context.month)}-01`;
        const query = {
            view: context.view,
            month: `${context.year}-${pad(context.month)}`,
            ...(context.isNetwork ? { name: context.entityKey } : { user_id: context.entityKey }),
        };
        setLoading(true);
        setErrors({});
        dispatch(fetchLegacyDataMonth(query))
            .unwrap()
            .then((days) => {
            const map = {};
            days.forEach((d) => { map[d.date] = d; });
            const dates = days.map((d) => d.date);
            setMonthMap(map);
            setAvailableDates(dates);
            // Default to the 1st if it has data, else the first day that does.
            setSelectedDate(dates.includes(firstOfMonth) ? firstOfMonth : dates[0] || firstOfMonth);
        })
            .catch(() => {
            setMonthMap({});
            setAvailableDates([]);
            setSelectedDate(firstOfMonth);
        })
            .finally(() => setLoading(false));
    }, [open, context, dispatch]);
    // Fill the fields from the selected day's stored values.
    useEffect(() => {
        const d = monthMap[selectedDate];
        setForm(d
            ? {
                outreach: String(d.outreach),
                booked: String(d.booked),
                happened: String(d.happened),
                enrollments: String(d.enrollments),
            }
            : EMPTY_FORM);
        setErrors({});
    }, [selectedDate, monthMap]);
    const hasData = !!monthMap[selectedDate];
    const setField = (key, value) => {
        setForm((prev) => ({ ...prev, [key]: value }));
        setErrors((prev) => ({ ...prev, [key]: "" }));
    };
    const validate = () => {
        const next = {};
        for (const { key, label } of MEASURE_FIELDS) {
            const v = Number(form[key]);
            if (form[key] === "" || !Number.isFinite(v) || v < 0) {
                next[key] = `${label} must be a non-negative number`;
            }
        }
        setErrors(next);
        return Object.keys(next).length === 0;
    };
    const handleSave = async () => {
        if (!context || !hasData || !validate())
            return;
        const action = await dispatch(updateLegacyDataCell({
            year: context.year,
            payload: {
                view: context.view,
                date: selectedDate,
                ...(context.isNetwork ? { name: context.entityKey } : { user_id: context.entityKey }),
                outreach: Number(form.outreach),
                booked: Number(form.booked),
                happened: Number(form.happened),
                enrollments: Number(form.enrollments),
            },
        }));
        if (updateLegacyDataCell.fulfilled.match(action))
            onClose();
    };
    const dateOptions = useMemo(() => availableDates, [availableDates]);
    if (!open || !context)
        return null;
    return (_jsxs(Dialog, { open: open, maxWidth: "sm", fullWidth: true, onClose: saving ? undefined : onClose, className: "sr-legacy-data-edit-modal", children: [_jsxs(Box, { className: "sr-legacy-data-edit-header", children: [_jsxs(Box, { className: "sr-legacy-data-edit-header-left", children: [_jsx("button", { type: "button", className: "sr-modal-close-btn", onClick: saving ? undefined : onClose, "aria-label": "Close", children: _jsx(X, { size: 18 }) }), _jsx(Box, { className: "sr-modal-separator" }), _jsxs(Typography, { className: "sr-modal-title", children: ["Edit Legacy Data - ", context.entityName] })] }), _jsx(AppButton, { variant: "contained", color: "primary", startIcon: _jsx(SaveIcon, {}), onClick: handleSave, disabled: saving || !hasData, children: saving ? "Saving..." : "Save" })] }), _jsx(DialogContent, { className: "sr-legacy-data-edit-content", children: loading ? (_jsx(Box, { className: "sr-legacy-data-edit-loader", children: _jsx(CommonLoader, { className: "sr-mini-loader" }) })) : (_jsxs(Box, { className: "sr-legacy-data-edit-body", children: [_jsx(Box, { className: "sr-legacy-data-edit-date", children: _jsx(FormField, { children: _jsx(SingleDatePicker, { label: "Date", value: toCalendarDate(selectedDate), onChange: (d) => d && setSelectedDate(fromCalendarDate(d)), enabledDates: dateOptions, allowToday: true, maxPastYears: 5, placeholder: "Select date" }) }) }), _jsxs(FormRow, { columns: 2, children: [_jsx(FormField, { children: _jsx(NumberInput, { label: "Enrollment", value: form.enrollments, onChange: (e) => setField("enrollments", e.target.value), min: 0, disabled: !hasData, error: !!errors.enrollments, errorMessage: errors.enrollments }) }), _jsx(FormField, { children: _jsx(NumberInput, { label: "Outreach", value: form.outreach, onChange: (e) => setField("outreach", e.target.value), min: 0, disabled: !hasData, error: !!errors.outreach, errorMessage: errors.outreach }) })] }), _jsxs(FormRow, { columns: 2, children: [_jsx(FormField, { children: _jsx(NumberInput, { label: "Meeting Booked", value: form.booked, onChange: (e) => setField("booked", e.target.value), min: 0, disabled: !hasData, error: !!errors.booked, errorMessage: errors.booked }) }), _jsx(FormField, { children: _jsx(NumberInput, { label: "Meetings Happened", value: form.happened, onChange: (e) => setField("happened", e.target.value), min: 0, disabled: !hasData, error: !!errors.happened, errorMessage: errors.happened }) })] }), !hasData && (_jsx(Typography, { className: "sr-legacy-data-edit-empty", children: "No data stored for this date \u2014 pick a date that isn't faded." })), _jsxs(Box, { className: "sr-legacy-data-edit-note", children: [_jsx(Typography, { className: "sr-legacy-data-edit-note-title", children: "Note:" }), _jsx("ul", { children: _jsx("li", { children: "Goal %, Booked, Conv. Rate, and Meetings Happened % are auto-calculated." }) })] })] })) })] }));
};
export default LegacyDataEditModal;
